import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import { getPatientDocuments } from '../../api/lineItemAppealApi';

/**
 * Modal listing patient-chart docs with Add/Added toggle states.
 * Props:
 *   open           — boolean
 *   onClose        — () => void
 *   patientId      — string|null
 *   providerId     — string|null
 *   selectedDocIds — Set<string>
 *   onToggleDoc    — (docId: string) => void
 */
export default function DocumentLibraryModal({
  open,
  onClose,
  patientId,
  providerId,
  selectedDocIds,
  onToggleDoc,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !patientId || !providerId) return;
    setLoading(true);
    setError(null);
    getPatientDocuments(patientId, providerId)
      .then((list) => setDocs(list))
      .catch(() => setError('Could not load patient documents.'))
      .finally(() => setLoading(false));
  }, [open, patientId, providerId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Choose from Patient Library</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}
        {!loading && !error && docs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No patient documents found.
          </Typography>
        )}
        {!loading && !error && docs.map((doc) => {
          const docId = String(doc.id);
          const added = selectedDocIds.has(docId);
          return (
            <Box
              key={docId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {doc.filename || 'Document'}
                </Typography>
                {doc.uploaded_at && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(doc.uploaded_at).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                variant={added ? 'contained' : 'outlined'}
                color={added ? 'success' : 'primary'}
                onClick={() => onToggleDoc(docId)}
                aria-label={added ? `Remove ${doc.filename}` : `Add ${doc.filename}`}
                sx={{ ml: 1, minWidth: 60, flexShrink: 0 }}
              >
                {added ? 'Added' : 'Add'}
              </Button>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" disableElevation>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
